import { addDays, startOfDay, subDays } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import { useIntl } from '@atlassian/jira-intl/src/index.tsx';
import messages from '@atlassian/jira-servicedesk-sla-panel/src/messages';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { useUserSubscriber } from '@atlassian/jira-user-services/src/main.tsx';
import type { TransformedGoalViewForSLAConfiguration, TransformedGoalView } from '../../types';

export const SlaFieldTransformer = (
	sla: TransformedGoalViewForSLAConfiguration,
): TransformedGoalView => {
	const {
		remainingTimeMillis,
		breachTime,
		goalTimeMillis,
		goalTimeLongFriendly,
		remainingTimeFriendly,
		date,
		isClosed,
		slaDisplayFormat,
	} = sla;

	const { formatMessage, formatDate } = useIntl();
	const [{ data: loggedInUser }] = useUserSubscriber();
	const userTimezone = loggedInUser?.timeZone || 'America/Toronto';
	let locale = useLocale();
	if (locale === undefined) {
		locale = 'en_US';
	}
	const localeString = locale.replace('_', '-');

	const dateTimeFormat = new Intl.DateTimeFormat(localeString, { hour: 'numeric' });
	const isBrowserLocale24h = () => !dateTimeFormat.format(0).match(/[A/P]M/);

	const getDays = () => {
		const calDate = new Date(date);
		return [
			startOfDay(calDate),
			startOfDay(addDays(calDate, 1)),
			startOfDay(subDays(calDate, 1)),
		] as const;
	};

	const isSame = (dateOne: Date, dateTwo: Date) =>
		dateOne.getDate() === dateTwo.getDate() &&
		dateOne.getMonth() === dateTwo.getMonth() &&
		dateOne.getFullYear() === dateTwo.getFullYear();

	const getMatchingDay = (receivedDate: Date, timeInEpochMillis: number) => {
		const [today, tomorrow, yesterday] = getDays();

		const isToday = isSame(today, receivedDate);
		const isTomorrow = isSame(tomorrow, receivedDate);
		const isYesterday = isSame(yesterday, receivedDate);

		let defaultDayFormat = formatDate(timeInEpochMillis, {
			timeZone: userTimezone,
			day: '2-digit',
			month: 'short',
		});

		isToday && (defaultDayFormat = formatMessage(messages.today));
		isTomorrow && (defaultDayFormat = formatMessage(messages.tomorrow));
		isYesterday && (defaultDayFormat = formatMessage(messages.yesterday));

		return defaultDayFormat;
	};

	const getTimeInSlaFriendlierFormatForSLAConfig = (
		timeInEpochMillis: number | null | undefined,
	) => {
		if (timeInEpochMillis) {
			const breachTimeTz = utcToZonedTime(timeInEpochMillis, userTimezone);
			return `${getMatchingDay(breachTimeTz, timeInEpochMillis)} ${formatDate(timeInEpochMillis, {
				hour12: !isBrowserLocale24h(),
				timeZone: userTimezone,
				hour: '2-digit',
				minute: '2-digit',
			})}`;
		}
		return '';
	};

	const getTimeRemainingInMinutes = (timeInHourSpaceMinutes: string): number => {
		const isTimeInMinus = timeInHourSpaceMinutes.indexOf('-') === 0;
		const absoluteTime = isTimeInMinus
			? timeInHourSpaceMinutes.substring(1)
			: timeInHourSpaceMinutes;

		const millis = parseInt(absoluteTime, 10);
		const totalMinutes = millis / (1000 * 60);
		return isTimeInMinus ? -1 * totalMinutes : totalMinutes;
	};

	const getPercentageOfTimeTaken = (goalTime: string, remainingTime: string) => {
		const goalTimeInMinutes = getTimeRemainingInMinutes(goalTime);
		const remainingTimeInMinutes = getTimeRemainingInMinutes(remainingTime);
		let percentage = (goalTimeInMinutes - remainingTimeInMinutes) / goalTimeInMinutes;
		percentage = Math.round(percentage * 100);
		return percentage.toString();
	};

	const getBreachTimeInLocalString = (breachTimeInEpochMillis: number): string => {
		if (breachTimeInEpochMillis) {
			const BREACH_DATE = utcToZonedTime(breachTimeInEpochMillis, userTimezone);
			return `${formatDate(breachTimeInEpochMillis, {
				hour12: !isBrowserLocale24h(),
				timeZone: userTimezone,
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
			})} GMT${format(BREACH_DATE, 'xx', { timeZone: userTimezone })}`;
		}
		return `${formatMessage(messages.notApplicable)}`;
	};

	const getHoverContentForSLAConfig = (
		isClosedSlaConfig: boolean,
		timeRemainingMillis: string,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		breachTimeMillisFormat: any,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		goalTimeMillisFormat: any,
		goalTimeLongFriendlyFormat: string,
		remainingTimeFriendlyFormat: string,
	): string => {
		const isTimeInMinus = remainingTimeFriendlyFormat.startsWith('-');
		let remainingTimeString;
		let timeStatus;
		if (isTimeInMinus) {
			remainingTimeString = remainingTimeFriendlyFormat.substring(1);
			timeStatus = formatMessage(messages.past);
		} else {
			remainingTimeString = remainingTimeFriendlyFormat;
			timeStatus = formatMessage(messages.left);
		}

		if (isClosedSlaConfig) {
			timeStatus = isTimeInMinus ? formatMessage(messages.breached) : formatMessage(messages.met);
			const percentage = getPercentageOfTimeTaken(goalTimeMillisFormat, timeRemainingMillis);
			const hoverContentCompleted = `${
				isTimeInMinus ? `-${remainingTimeString}` : remainingTimeString
			} ${timeStatus} ${String.fromCodePoint(
				0x2022,
			)} ${percentage}% of ${goalTimeLongFriendlyFormat} ${String.fromCodePoint(
				0x2022,
			)} ${getBreachTimeInLocalString(breachTimeMillisFormat)}`;
			return hoverContentCompleted;
		}

		const hoverContent = `${remainingTimeString} ${timeStatus} ${String.fromCodePoint(
			0x2022,
		)} ${formatMessage(messages.due)}: ${getBreachTimeInLocalString(breachTimeMillisFormat)}`;

		return hoverContent;
	};

	const timeFriendly = getTimeInSlaFriendlierFormatForSLAConfig(breachTime);

	const hoverFriendly = getHoverContentForSLAConfig(
		isClosed,
		remainingTimeMillis,
		breachTime,
		goalTimeMillis,
		goalTimeLongFriendly,
		remainingTimeFriendly,
	);

	if (slaDisplayFormat === 'OLD_SLA_FORMAT') {
		return sla;
	}

	return { ...sla, timeRemainingFriendly: timeFriendly, hoverContent: hoverFriendly };
};
